import { TxnState } from '@global/constants/verification';
import { convertToTitleCase } from '@global/helpers/general';
import {
  Cancel,
  CheckCircle,
  Error,
  HourglassTop,
  Lock,
  Pending,
  PlayArrow,
  Reply
} from '@mui/icons-material';
import { Box, SxProps, Typography } from '@mui/material';
import React from 'react';

interface StatusTagProps {
  status: TxnState;
  sx?: SxProps;
  selected?: boolean;
  clickable?: boolean;
}

interface TagData {
  icon: React.ReactNode | undefined;
  text: string;
  bgColor: string;
  color: string;
  theme?: string;
  selected?: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const getTagStructuredData = (
  status: StatusTagProps['status'],
  selected = false
): TagData => {
  const data: TagData = {
    icon: undefined,
    text: convertToTitleCase(status?.toLocaleLowerCase()).replace('_', ' '),
    bgColor: '#F5F5F5',
    color: '#66747F'
  };
  const getColor = (color: string) => (selected ? '#ffffff' : color);
  switch (status) {
    //verification is not yet started
    case 'NOT_INITIALIZED':
      data.color = '#404052';
      data.bgColor = '#F5F5F5';
      data.icon = <PlayArrow sx={{ fontSize: '1.6rem', color: getColor(data.color) }} />;
      data.text = 'Not Initialized';
      break;

    case 'SUBMITTED': //verification was submitted from user end, no further action is needed
    case 'DATA_SAVED': //verification data is saved in users' profile/wallet
      data.color = '#044DA1';
      data.bgColor = '#E4F1FF';
      data.icon = <CheckCircle sx={{ fontSize: '1.6rem', color: getColor(data.color) }} />;
      data.text = 'Submitted';
      data.theme = 'primary';
      break;

    case 'IN_PROGRESS': //verification is under verification
      data.color = '#FAA42B';
      data.bgColor = '#FFEFDC';
      data.icon = <Pending sx={{ fontSize: '1.6rem', color: getColor(data.color) }} />;
      break;

    case 'INSUFFICIENT_DATA': //verification has insufficiency
      data.color = '#C84646';
      data.bgColor = '#F9ECEC';
      data.icon = <Error sx={{ fontSize: '1.6rem', color: getColor(data.color) }} />;
      data.text = 'Action Required';
      break;

    case 'FAILED': //verification was failed(could not verify its authenticity)
      data.color = '#782A2A';
      data.bgColor = '#F9ECEC';
      data.icon = <Cancel sx={{ fontSize: '1.6rem', color: getColor(data.color) }} />;
      break;

    case 'COMPLETED': //verification is finally at its final state(could mean both- verified/failed)
    case 'VERIFIED': //verification is verified
      data.color = '#239D56';
      data.bgColor = '#E9F5EE';
      data.icon = <CheckCircle sx={{ fontSize: '1.6rem', color: getColor(data.color) }} />;
      data.text = 'Verified';
      break;

    case 'SKIPPED': //verification was skipped, user can fill it again
      data.color = '#052242';
      data.bgColor = '#E4F1FF';
      data.icon = <CheckCircle sx={{ fontSize: '1.6rem', color: getColor(data.color) }} />;
      data.text = 'Skipped';
      break;

    case 'SUCCESS': //verification/or the thing which this state is referring to is at a successful state(could be payment, etc.)
      data.color = '#239D56';
      data.bgColor = '#E9F5EE'; // Add if there's a specific background color
      data.icon = <CheckCircle sx={{ fontSize: '1.6rem', color: getColor(data.color) }} />;
      data.text = 'Success';
      data.theme = 'success';
      break;

    case 'PENDING': //verification/or the thing which this state is referring to is at a pending state, it could fail, or succeed(could be payment, etc.)
      data.color = '#FAA42B';
      data.bgColor = '#FEF8EC'; // Add background color if needed
      data.icon = <HourglassTop sx={{ fontSize: '1.6rem', color: getColor(data.color) }} />;
      data.text = 'Pending';
      data.theme = 'warning';
      break;

    case 'READY_TO_SUBMIT': //verification is present in profile/wallet, but not yet submitted/sent on this particular form/transaction
      data.color = '#0050ff';
      data.bgColor = '#E4F1FF';
      data.icon = <CheckCircle sx={{ fontSize: '1.6rem', color: getColor(data.color) }} />;
      data.text = 'Ready To Submit';
      data.theme = 'primary';
      break;

    case 'CLOSED': //verification is closed(not verified, not failed, but reached a terminal state)
      data.color = '#824700';
      data.bgColor = '#FFEFDC';
      data.icon = <Lock sx={{ fontSize: '1.6rem', color: getColor(data.color) }} />;
      data.text = 'Closed';
      data.theme = 'brown';
      break;

    case 'REFUNDED': //the thing which this state is referring to was refunded(could be payment, etc.)
      data.color = '#073B72';
      data.bgColor = '#E4F1FF';
      data.icon = <Reply sx={{ fontSize: '1.6rem', color: getColor(data.color) }} />;
      data.text = 'Refunded';
      data.theme = 'primary';
      break;
  }

  return data;
};

const StatusTag: React.FC<StatusTagProps> = ({ status, sx = {}, selected = false, clickable }) => {
  const data = getTagStructuredData(status, selected);

  const getBgColor = () => {
    if (!clickable) {
      return 'transparent';
    }
    return selected ? data.color : '#fff';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        bgcolor: getBgColor(),
        boxShadow: clickable ? '1px 4px 10px 0px rgba(173, 173, 173, 0.16)' : 'none',
        gap: '.4rem',
        p: '.2rem .6rem .2rem .4rem',
        borderRadius: '4px',
        ...sx
      }}>
      {data.icon}
      <Typography
        sx={{
          fontSize: '1.2rem',
          fontWeight: 600,
          lineHeight: '1.2rem',
          color: selected ? '#ffffff' : data.color,
          whiteSpace: 'nowrap'
        }}>
        {data.text}
      </Typography>
    </Box>
  );
};

export default StatusTag;
